import type { DrawingEntity, Project, Vec3 } from '../types';
import { migrateProject } from './migrations';
import { linePlanPoints } from '../geometry/line3d';
import { faceBasis } from '../geometry/faces';
import { roomOutline, validRoomOutline } from '../geometry/roomOutline';

const pair = (code: number, value: string | number) => `${code}\n${value}\n`;
const finite = (value: unknown) => typeof value === 'number' && Number.isFinite(value);
const point = (value: unknown): value is [number, number] => Array.isArray(value) && value.length === 2 && value.every(finite);
const point3d = (value: unknown): value is Vec3 => Array.isArray(value) && value.length === 3 && value.every(finite);

export function parseProjectFile(contents: string): Project {
  const raw: unknown = JSON.parse(contents);
  if (!raw || typeof raw !== 'object') throw new Error('Invalid project file');
  const data = raw as Record<string, unknown>;
  if (typeof data.id !== 'string' || typeof data.name !== 'string' || !data.name.trim() ||
    !Array.isArray(data.furniture) || !Array.isArray(data.materials) || !Array.isArray(data.drawings) ||
    !data.room || typeof data.room !== 'object' || !data.settings || typeof data.settings !== 'object') {
    throw new Error('Invalid project file');
  }
  const project = migrateProject(data as unknown as Project);
  if (!finite(project.room.width) || !finite(project.room.length) || !finite(project.room.height) ||
    project.room.width <= 0 || project.room.length <= 0 || project.room.height <= 0 ||
    (project.room.outline !== undefined && (!Array.isArray(project.room.outline) || !validRoomOutline(project.room.outline))) ||
    !project.drawings.every((drawing) => drawing && ['line', 'polyline', 'rectangle', 'circle', 'dimension', 'face'].includes(drawing.kind) && Array.isArray(drawing.points) && drawing.points.length >= 2 && drawing.points.every(point) && (drawing.points3d === undefined || (drawing.kind === 'line' && Array.isArray(drawing.points3d) && drawing.points3d.length === 2 && drawing.points3d.every(point3d))) && (drawing.kind !== 'face' || (Array.isArray(drawing.vertices3d) && drawing.vertices3d.length >= 3 && drawing.vertices3d.every(point3d) && !!faceBasis(drawing.vertices3d) && (drawing.extrusionMm === undefined || finite(drawing.extrusionMm))))) ||
    !project.furniture.every((item) => item && typeof item.id === 'string' && finite(item.width) && finite(item.height) && finite(item.depth) && item.width > 0 && item.height > 0 && item.depth > 0 && Array.isArray(item.position) && item.position.length === 3 && item.position.every(finite))) {
    throw new Error('Invalid project geometry');
  }
  project.drawings = project.drawings.map((drawing) => drawing.vertices3d ? { ...drawing, points: drawing.vertices3d.map(([x, , z]) => [x, z]) } : drawing.points3d ? { ...drawing, points: linePlanPoints(drawing.points3d) } : drawing);
  return project;
}

export async function createPortableProjectFile(project: Project, loadTexture: (id: string, path?: string) => Promise<Blob | undefined> = async () => undefined): Promise<string> {
  const portable = structuredClone(project);
  for (const material of portable.materials) {
    if (!material.textureAssetId) continue;
    const blob = await loadTexture(material.textureAssetId, material.textureStoragePath);
    if (!blob) {
      if (!material.textureUrl) throw new Error(`Missing texture: ${material.name}`);
      continue;
    }
    material.textureUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
    material.textureAssetId = undefined;
    material.textureStoragePath = undefined;
  }
  return JSON.stringify(portable, null, 2);
}

const line = (a: [number, number], b: [number, number], layer: string) =>
  pair(0, 'LINE') + pair(8, layer) + pair(10, a[0]) + pair(20, -a[1]) + pair(30, 0) + pair(11, b[0]) + pair(21, -b[1]) + pair(31, 0);

const spatialLine = (a: Vec3, b: Vec3) =>
  pair(0, 'LINE') + pair(8, 'DRAWING_3D') + pair(10, a[0]) + pair(20, -a[2]) + pair(30, a[1]) + pair(11, b[0]) + pair(21, -b[2]) + pair(31, b[1]);

function drawingEntities(drawing: DrawingEntity): string {
  const points = drawing.points;
  if (points.length < 2) return '';
  if (drawing.kind === 'line' && drawing.points3d) return spatialLine(drawing.points3d[0], drawing.points3d[1]);
  if (drawing.kind === 'face' && drawing.vertices3d) {
    const boundary = drawing.vertices3d;
    const normal = faceBasis(boundary)?.normal;
    const offset = normal?.multiplyScalar(drawing.extrusionMm ?? 0);
    const top = offset ? boundary.map(([x, y, z]) => [x + offset.x, y + offset.y, z + offset.z] as Vec3) : [];
    const edges = boundary.map((point, index) => spatialLine(point, boundary[(index + 1) % boundary.length]));
    if (top.length) edges.push(...top.map((point, index) => spatialLine(point, top[(index + 1) % top.length])), ...boundary.map((point, index) => spatialLine(point, top[index])));
    return edges.join('');
  }
  if (drawing.kind === 'dimension') {
    const [a, b] = points;
    const label = `${Math.round(Math.hypot(b[0] - a[0], b[1] - a[1]) * 1000) / 1000} mm`;
    return line(a, b, 'DIMENSIONS') + pair(0, 'TEXT') + pair(8, 'DIMENSIONS') + pair(10, (a[0] + b[0]) / 2) + pair(20, -(a[1] + b[1]) / 2) + pair(30, 0) + pair(40, 80) + pair(1, label);
  }
  if (drawing.kind === 'circle') {
    const radius = Math.hypot(points[1][0] - points[0][0], points[1][1] - points[0][1]);
    return radius > 0 ? pair(0, 'CIRCLE') + pair(8, 'DRAWING') + pair(10, points[0][0]) + pair(20, -points[0][1]) + pair(30, 0) + pair(40, radius) : '';
  }
  const vertices = drawing.kind === 'rectangle'
    ? [points[0], [points[1][0], points[0][1]], points[1], [points[0][0], points[1][1]], points[0]] as [number, number][]
    : points;
  return vertices.slice(1).map((end, index) => line(vertices[index], end, 'DRAWING')).join('');
}

export function createDxf(project: Project): string {
  const room = project.room;
  let entities = '';
  if (room.visible !== false) {
    const corners = [...roomOutline(room), roomOutline(room)[0]];
    entities += corners.slice(1).map((end, index) => line(corners[index], end, 'ROOM')).join('');
  }
  entities += project.drawings.map(drawingEntities).join('');
  for (const item of project.furniture.filter((entry) => entry.visible)) {
    const c = Math.cos(item.rotation[1]), s = Math.sin(item.rotation[1]);
    const corners: [number, number][] = [[-item.width / 2, -item.depth / 2], [item.width / 2, -item.depth / 2], [item.width / 2, item.depth / 2], [-item.width / 2, item.depth / 2], [-item.width / 2, -item.depth / 2]]
      .map(([x, z]) => [item.position[0] + x * c - z * s, item.position[2] + x * s + z * c]);
    entities += corners.slice(1).map((end, index) => line(corners[index], end, 'FURNITURE')).join('');
  }
  return pair(0, 'SECTION') + pair(2, 'HEADER') + pair(9, '$ACADVER') + pair(1, 'AC1009') + pair(9, '$INSUNITS') + pair(70, 4) + pair(0, 'ENDSEC') + pair(0, 'SECTION') + pair(2, 'ENTITIES') + entities + pair(0, 'ENDSEC') + pair(0, 'EOF');
}

export function downloadText(filename: string, contents: string, type: string) {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const safeFilename = (name: string) => name.trim().replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '') || 'project';
